<script lang="ts">
	import { onMount } from 'svelte';
	import { footerLinks } from '$lib/data/nav';
	import logo from '$lib/assets/img/admiral-logo.svg';
	import { getStatusSnapshot } from '$lib/status/client';
	import type { StatusLevel } from '$lib/status/types';

	const STATUS_META: Record<StatusLevel, { color: string; label: string }> = {
		operational: { color: '#10B981', label: 'All systems operational' },
		degraded: { color: '#F59E0B', label: 'Degraded performance' },
		down: { color: '#EF4444', label: 'Service disruption' },
		maintenance: { color: '#6366F1', label: 'Under maintenance' },
		unknown: { color: '#9CA3AF', label: 'Status unavailable' }
	};

	let overall: StatusLevel = 'unknown';

	onMount(async () => {
		try {
			const snapshot = await getStatusSnapshot();
			overall = snapshot.overall ?? 'unknown';
		} catch {
			/* leave as unknown */
		}
	});

	$: status = STATUS_META[overall];
</script>

<footer id="contact" class="mt-20 border-t border-white/[0.06] sm:mt-32 lg:mt-40">
	<div class="mx-auto max-w-[1216px] px-4 py-12 sm:py-16 lg:px-8 lg:py-20">
		<div class="flex flex-col gap-10 lg:flex-row lg:justify-between">
			<!-- Brand block -->
			<div class="max-w-sm">
				<img class="mb-6 w-24" src={logo} alt="Admiral" />
				<p class="max-w-[280px] font-display text-sm leading-relaxed text-white/55">
					The operating system for edge infrastructure. Built in Australia for global operators.
				</p>
				<div class="mt-6 flex gap-2">
					<a
						href="https://www.linkedin.com/company/qdyn"
						target="_blank"
						rel="noopener"
						aria-label="LinkedIn"
						class="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-white/60 transition hover:border-white/30 hover:text-white"
					>
						<svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"
							><path
								d="M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14zM8.339 18V9.924H5.67V18h2.669zM7.004 8.754c.857 0 1.552-.706 1.552-1.577 0-.87-.695-1.577-1.552-1.577-.858 0-1.553.707-1.553 1.577 0 .871.695 1.577 1.553 1.577zM18 18v-4.424c0-2.313-.5-4.09-3.203-4.09-1.299 0-2.171.713-2.528 1.388h-.036V9.924H9.672V18h2.669v-3.995c0-1.054.2-2.073 1.507-2.073 1.288 0 1.306 1.204 1.306 2.14V18H18z"
							/></svg
						>
					</a>
					<a
						href="https://x.com/qdynamicsco"
						target="_blank"
						rel="noopener"
						aria-label="Twitter"
						class="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-white/60 transition hover:border-white/30 hover:text-white"
					>
						<svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"
							><path
								d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
							/></svg
						>
					</a>
					<a
						href="https://github.com/qdynamicsco"
						target="_blank"
						rel="noopener"
						aria-label="GitHub"
						class="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-white/60 transition hover:border-white/30 hover:text-white"
					>
						<svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"
							><path
								d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.085 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.4 3-.405 1.02.005 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.627-5.373-12-12-12"
							/></svg
						>
					</a>
				</div>
			</div>

			<!-- Link sections: responsive grid that never half-wraps -->
			<div class="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:flex lg:gap-x-12 xl:gap-x-16">
				{#each Object.entries(footerLinks) as [section, links]}
					<div class="min-w-[120px]">
						<h4 class="mb-4 font-display text-sm font-medium text-white">{section}</h4>
						<ul class="flex flex-col gap-3">
							{#each links as link}
								<li>
									<a
										href={link.href}
										target={link.external ? '_blank' : undefined}
										rel={link.external ? 'noopener' : undefined}
										class="font-display text-sm text-white/55 transition hover:text-white"
									>
										{link.label}
									</a>
								</li>
							{/each}
						</ul>
					</div>
				{/each}
			</div>
		</div>

		<!-- Bottom bar -->
		<div
			class="mt-12 flex flex-col items-start justify-between gap-4 border-t border-white/[0.06] pt-8 sm:mt-16 sm:flex-row sm:items-center"
		>
			<p class="font-display text-xs text-white/40">&copy; 2026 Admiral. All rights reserved.</p>
			<a
				href="/status"
				class="flex items-center gap-2 transition hover:opacity-80"
				aria-label="View status page"
			>
				<span
					class="inline-block h-2 w-2 rounded-full"
					style="background-color: {status.color}; box-shadow: 0 0 8px {status.color}"
				></span>
				<span class="font-display text-xs text-white/40">{status.label}</span>
			</a>
		</div>
	</div>
</footer>
